<script setup>
import { computed, defineModel, defineProps, onMounted, ref, watch } from 'vue';
import MultiSelectDropdown from '@/components/UI/MultiSelectDropdown.vue';
import SelectDropdown from '@/components/UI/SelectDropdown.vue';
import PlusSvg from '@/components/UI/svg/PlusSvg.vue';
import RefreshSvg from '@/components/UI/svg/RefreshSvg.vue';
import LoadingSvg from '@/components/UI/LoadingSvg.vue';
import BitrixDealConditionItem from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixDealConditionItem.vue';
import UploadDateRangeFields from '@/components/CreateConnectionComponents/UploadDateRangeFields.vue';
import { Api } from '@/service/api';
import { createToaster } from '@meforma/vue-toaster';

const props = defineProps({
    account: {
        type: [String, Object],
        default: null,
    },
    entityType: {
        type: String,
        default: 'DEALS',
    },
    title: {
        type: String,
        required: true,
    },
    description: {
        type: String,
        default: '',
    },
    showDirections: {
        type: Boolean,
        default: true,
    },
    showStages: {
        type: Boolean,
        default: true,
    },
});

const directions = defineModel('directions', { default: () => [] });
const stages = defineModel('stages', { default: () => [] });
const conditionType = defineModel('conditionType', { default: 'AND' });
const conditions = defineModel('conditions', { default: () => [] });
const dateField = defineModel('dateField', { default: 'DATE_CREATE' });
const startDate = defineModel('startDate', {
    default: () => ({ type: 'exact', date: null }),
});
const endDate = defineModel('endDate', {
    default: () => ({ type: 'exact', date: null }),
});

const toaster = createToaster();

const entityLabel = computed(() => {
    if (props.entityType === 'LEADS') {
        return 'лиды';
    }
    return 'сделки';
});

const dateFieldOptions = computed(() => [
    {
        value: 'DATE_CREATE',
        label: 'Дата создания',
        description: `Фильтровать ${entityLabel.value} по дате создания`,
    },
    {
        value: 'DATE_MODIFY',
        label: 'Дата обновления',
        description: `Фильтровать ${entityLabel.value} по дате изменения`,
    },
]);

const dateRangeIdPrefix = computed(() => {
    return props.entityType === 'LEADS' ? 'bitrix-leads' : 'bitrix-deals';
});

const isSyncingDirections = ref(false);
const isSyncingStages = ref(false);
const isSyncingFields = ref(false);

const fallbackDirectionOptions = [
    { id: 'sales', name: 'Продажи' },
    { id: 'marketing', name: 'Маркетинг' },
    { id: 'support', name: 'Поддержка' },
];

const directionOptions = ref([]);
const stageOptions = ref([]);
const fieldOptions = ref([]);
const criteria = ref([]);

const conditionTypes = ref([
    {
        name: 'Данные соответствуют нескольким условиям одновременно (AND)',
        type: 'AND',
    },
    {
        name: 'Данные соответствуют одному из условий (OR)',
        type: 'OR',
    },
]);

const conditionTypeValue = computed({
    get: () => conditionTypes.value.find((item) => item.type === conditionType.value) || conditionTypes.value[0],
    set: (value) => {
        conditionType.value = value?.type || 'AND';
    },
});

const stageGridClass = computed(() => {
    return props.showDirections
        ? 'grid grid-cols-1 lg:grid-cols-2 gap-4'
        : 'grid grid-cols-1 gap-4';
});

const isSyncingAny = computed(() => {
    return isSyncingDirections.value || isSyncingStages.value || isSyncingFields.value;
});

const showDirectionsSync = computed(() => props.showDirections && props.entityType === 'DEALS');
const showStagesSync = computed(() => props.showStages && (props.entityType === 'DEALS' || props.entityType === 'LEADS'));
const showFieldsSync = computed(() => props.entityType === 'DEALS' || props.entityType === 'LEADS');

function normalizeOptionId(value) {
    if (value == null || value === '') {
        return null;
    }
    return String(value);
}

function normalizeOptions(items = []) {
    return items.map((item) => {
        const id = normalizeOptionId(item?.id ?? item?.value ?? item?.code);
        if (id == null) {
            return null;
        }

        const name = item?.name ?? item?.label ?? item?.title ?? id;
        return { id, name };
    }).filter(Boolean);
}

function mergeOptionsWithSelected(options = [], selected = []) {
    const byId = new Map();

    normalizeOptions(options).forEach((item) => {
        byId.set(item.id, item);
    });

    (selected || []).forEach((item) => {
        const id = normalizeOptionId(item?.id);
        if (id == null || byId.has(id)) {
            return;
        }
        byId.set(id, {
            id,
            name: item?.name || id,
        });
    });

    return Array.from(byId.values());
}

function enrichSelectedFromOptions(selected = [], options = []) {
    const byId = new Map(normalizeOptions(options).map((item) => [item.id, item]));

    return (selected || [])
        .map((item) => {
            const id = normalizeOptionId(item?.id);
            if (id == null) {
                return null;
            }

            const matched = byId.get(id);
            return matched
                ? { ...item, id: matched.id, name: matched.name }
                : { ...item, id, name: item?.name || id };
        })
        .filter(Boolean);
}

function getAccountId() {
    if (!props.account) {
        return null;
    }

    if (typeof props.account === 'string' || typeof props.account === 'number') {
        return props.account;
    }

    return props.account.id || props.account.domain || props.account.portal || null;
}

function requireAccount({ silent = false } = {}) {
    const accountId = getAccountId();
    if (!accountId) {
        if (!silent) {
            toaster.error('Сначала выберите аккаунт Bitrix');
        }
        return null;
    }
    return accountId;
}

function preserveSelectedInOptions() {
    directionOptions.value = mergeOptionsWithSelected(directionOptions.value, directions.value);
    stageOptions.value = mergeOptionsWithSelected(stageOptions.value, stages.value);
    fieldOptions.value = mergeOptionsWithSelected(
        fieldOptions.value,
        (conditions.value || []).map((condition) => condition.field).filter(Boolean),
    );

    directions.value = enrichSelectedFromOptions(directions.value, directionOptions.value);
    stages.value = enrichSelectedFromOptions(stages.value, stageOptions.value);
}

function pruneSelectedToOptions() {
    const directionIds = new Set(directionOptions.value.map((item) => item.id));
    const stageIds = new Set(stageOptions.value.map((item) => item.id));
    const fieldIds = new Set(fieldOptions.value.map((item) => item.id));

    if (directionIds.size > 0) {
        directions.value = enrichSelectedFromOptions(
            (directions.value || []).filter((item) => directionIds.has(normalizeOptionId(item.id))),
            directionOptions.value,
        );
    } else {
        directions.value = enrichSelectedFromOptions(directions.value, directionOptions.value);
    }

    if (stageIds.size > 0) {
        stages.value = enrichSelectedFromOptions(
            (stages.value || []).filter((item) => stageIds.has(normalizeOptionId(item.id))),
            stageOptions.value,
        );
    } else {
        stages.value = enrichSelectedFromOptions(stages.value, stageOptions.value);
    }

    if (fieldIds.size === 0) {
        return;
    }

    conditions.value = (conditions.value || []).map((condition) => {
        const fieldId = normalizeOptionId(condition.field?.id);
        if (fieldId && !fieldIds.has(fieldId)) {
            return {
                ...condition,
                field: {},
            };
        }

        const matchedField = fieldOptions.value.find((item) => item.id === fieldId);
        if (!matchedField) {
            return condition;
        }

        return {
            ...condition,
            field: {
                ...condition.field,
                id: matchedField.id,
                name: matchedField.name,
            },
        };
    });
}

function applyDirectionOptions(items, { prune = false, allowFallback = false } = {}) {
    const normalized = normalizeOptions(items);
    if (normalized.length) {
        directionOptions.value = prune
            ? normalized
            : mergeOptionsWithSelected(normalized, directions.value);
    } else if (allowFallback) {
        directionOptions.value = mergeOptionsWithSelected(
            [...fallbackDirectionOptions],
            directions.value,
        );
    } else {
        directionOptions.value = mergeOptionsWithSelected([], directions.value);
    }

    if (prune && normalized.length) {
        pruneSelectedToOptions();
    } else {
        preserveSelectedInOptions();
    }
}

function applyStageOptions(items, { prune = false } = {}) {
    const normalized = normalizeOptions(items);
    const uniqueById = new Map();
    normalized.forEach((item) => {
        uniqueById.set(item.id, item);
    });
    const uniqueOptions = Array.from(uniqueById.values());

    stageOptions.value = prune
        ? uniqueOptions
        : mergeOptionsWithSelected(uniqueOptions, stages.value);

    if (prune && uniqueOptions.length) {
        pruneSelectedToOptions();
    } else {
        preserveSelectedInOptions();
    }
}

function applyFieldOptions(items, { prune = false } = {}) {
    const normalized = normalizeOptions(items);
    const selectedFields = (conditions.value || [])
        .map((condition) => condition.field)
        .filter(Boolean);

    fieldOptions.value = prune
        ? normalized
        : mergeOptionsWithSelected(normalized, selectedFields);

    if (prune && normalized.length) {
        pruneSelectedToOptions();
    }
}

function getSelectedDirectionIds() {
    return (directions.value || [])
        .map((item) => normalizeOptionId(item?.id))
        .filter((id) => id != null);
}

async function syncCriteria({ silent = true } = {}) {
    try {
        const [criteriaResponse] = await Promise.allSettled([Api.getFilterCriteria()]);
        if (criteriaResponse.status === 'fulfilled') {
            criteria.value = criteriaResponse.value || [];
            return true;
        }
        if (!silent) {
            toaster.error('Не удалось синхронизировать критерии фильтра');
        }
        return false;
    } catch (error) {
        console.error('Failed to sync filter criteria', error);
        if (!silent) {
            toaster.error('Не удалось синхронизировать критерии фильтра');
        }
        return false;
    }
}

async function syncDirections({ silent = false } = {}) {
    if (!showDirectionsSync.value || isSyncingDirections.value) {
        return true;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        applyDirectionOptions([], { allowFallback: true });
        return false;
    }

    isSyncingDirections.value = true;

    try {
        const [directionsResponse] = await Promise.allSettled([
            Api.getBitrixDealDirections(accountId),
        ]);

        if (directionsResponse.status === 'fulfilled' && Array.isArray(directionsResponse.value)) {
            applyDirectionOptions(directionsResponse.value, { prune: true });
            if (!silent) {
                toaster.success('Направления синхронизированы');
            }
            return true;
        }

        applyDirectionOptions([], { allowFallback: true });
        if (!silent) {
            toaster.error('Не удалось синхронизировать направления');
        }
        return false;
    } catch (error) {
        console.error('Failed to sync Bitrix directions', error);
        applyDirectionOptions([], { allowFallback: true });
        if (!silent) {
            toaster.error('Не удалось синхронизировать направления');
        }
        return false;
    } finally {
        isSyncingDirections.value = false;
    }
}

async function syncDealStages({ domain, silent = false } = {}) {
    if (!(props.showStages && props.entityType === 'DEALS')) {
        return true;
    }

    const accountId = domain || getAccountId();
    const directionIds = getSelectedDirectionIds();

    if (!accountId || directionIds.length === 0) {
        // Keep already selected stages visible on update load until directions are ready.
        stageOptions.value = mergeOptionsWithSelected([], stages.value);
        if (!silent && directionIds.length === 0) {
            stages.value = [];
            stageOptions.value = [];
        }
        return true;
    }

    const stageResponses = await Promise.allSettled(
        directionIds.map((directionId) => Api.getBitrixDealStages(accountId, directionId)),
    );

    const mergedStages = stageResponses
        .filter((response) => response.status === 'fulfilled' && Array.isArray(response.value))
        .flatMap((response) => response.value);

    const hasSuccessfulResponse = stageResponses.some((response) => response.status === 'fulfilled');

    if (hasSuccessfulResponse) {
        applyStageOptions(mergedStages, { prune: true });
        return true;
    }

    stageOptions.value = mergeOptionsWithSelected([], stages.value);
    if (!silent) {
        toaster.error('Не удалось синхронизировать стадии Bitrix');
    }
    return false;
}

async function syncLeadStages({ domain, silent = false } = {}) {
    if (!(props.showStages && props.entityType === 'LEADS')) {
        return true;
    }

    const accountId = domain || getAccountId();

    if (!accountId) {
        stageOptions.value = mergeOptionsWithSelected([], stages.value);
        return true;
    }

    const [stagesResponse] = await Promise.allSettled([
        Api.getBitrixLeadStages(accountId),
    ]);

    if (stagesResponse.status === 'fulfilled' && Array.isArray(stagesResponse.value)) {
        applyStageOptions(stagesResponse.value, { prune: true });
        return true;
    }

    stageOptions.value = mergeOptionsWithSelected([], stages.value);
    if (!silent) {
        toaster.error('Не удалось синхронизировать стадии Bitrix');
    }
    return false;
}

async function syncStages({ silent = false } = {}) {
    if (!showStagesSync.value || isSyncingStages.value) {
        return true;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        stageOptions.value = mergeOptionsWithSelected([], stages.value);
        return false;
    }

    if (props.entityType === 'DEALS' && getSelectedDirectionIds().length === 0) {
        stageOptions.value = mergeOptionsWithSelected([], stages.value);
        if (!silent) {
            stages.value = [];
            stageOptions.value = [];
            toaster.error('Сначала выберите направление');
        }
        return silent;
    }

    isSyncingStages.value = true;

    try {
        const synced = props.entityType === 'DEALS'
            ? await syncDealStages({ domain: accountId, silent })
            : await syncLeadStages({ domain: accountId, silent });

        preserveSelectedInOptions();

        if (synced && !silent) {
            toaster.success('Стадии синхронизированы');
        }

        return synced;
    } catch (error) {
        console.error('Failed to sync Bitrix stages', error);
        preserveSelectedInOptions();
        if (!silent) {
            toaster.error('Не удалось синхронизировать стадии Bitrix');
        }
        return false;
    } finally {
        isSyncingStages.value = false;
    }
}

async function syncFields({ silent = false } = {}) {
    if (!showFieldsSync.value || isSyncingFields.value) {
        return true;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        fieldOptions.value = mergeOptionsWithSelected(
            [],
            (conditions.value || []).map((condition) => condition.field).filter(Boolean),
        );
        return false;
    }

    isSyncingFields.value = true;

    try {
        await syncCriteria({ silent: true });

        const fieldsPromise = props.entityType === 'DEALS'
            ? Api.getBitrixDealFields(accountId)
            : Api.getBitrixLeadFields(accountId);

        const [fieldsResponse] = await Promise.allSettled([fieldsPromise]);

        if (fieldsResponse.status === 'fulfilled' && Array.isArray(fieldsResponse.value)) {
            applyFieldOptions(fieldsResponse.value, { prune: true });
            if (!silent) {
                toaster.success('Поля синхронизированы');
            }
            return true;
        }

        fieldOptions.value = mergeOptionsWithSelected(
            [],
            (conditions.value || []).map((condition) => condition.field).filter(Boolean),
        );
        if (!silent) {
            toaster.error('Не удалось синхронизировать поля Bitrix');
        }
        return false;
    } catch (error) {
        console.error('Failed to sync Bitrix fields', error);
        fieldOptions.value = mergeOptionsWithSelected(
            [],
            (conditions.value || []).map((condition) => condition.field).filter(Boolean),
        );
        if (!silent) {
            toaster.error('Не удалось синхронизировать поля Bitrix');
        }
        return false;
    } finally {
        isSyncingFields.value = false;
    }
}

async function syncFromBackend({ silent = false } = {}) {
    if (isSyncingAny.value) {
        return;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        preserveSelectedInOptions();
        return;
    }

    const tasks = [];

    if (showDirectionsSync.value) {
        tasks.push(syncDirections({ silent: true }));
    }
    if (showFieldsSync.value) {
        tasks.push(syncFields({ silent: true }));
    }

    const results = await Promise.all(tasks);

    // Stages depend on selected directions for deals, so run after directions.
    let stagesSynced = true;
    if (showStagesSync.value) {
        stagesSynced = await syncStages({ silent: true });
    }

    preserveSelectedInOptions();

    const allSynced = results.every(Boolean) && stagesSynced;

    if (!silent) {
        if (allSynced) {
            toaster.success('Данные Bitrix синхронизированы');
        } else {
            toaster.error('Не удалось синхронизировать данные Bitrix');
        }
    }
}

watch(
    () => (directions.value || []).map((item) => normalizeOptionId(item?.id)).filter(Boolean).join(','),
    async (currentIds, previousIds) => {
        if (!(props.showStages && props.entityType === 'DEALS')) {
            return;
        }

        // Skip the initial hydrate tick so update-load selections are not wiped.
        if (previousIds === undefined) {
            return;
        }

        await syncStages({ silent: true });
        preserveSelectedInOptions();
    },
);

watch(
    () => getAccountId(),
    async (accountId, previousAccountId) => {
        if (!accountId || accountId === previousAccountId) {
            return;
        }

        await syncFromBackend({ silent: true });
    },
);

onMounted(async () => {
    if (!conditionType.value) {
        conditionType.value = 'AND';
    }

    if (!Array.isArray(conditions.value)) {
        conditions.value = [];
    }

    if (!Array.isArray(directions.value)) {
        directions.value = [];
    }

    if (!Array.isArray(stages.value)) {
        stages.value = [];
    }

    // Immediately show previously selected values from GET connection.
    preserveSelectedInOptions();
    await syncFromBackend({ silent: true });
});

function addCondition() {
    conditions.value = [
        ...conditions.value,
        {
            id: Date.now() + conditions.value.length,
            field: {},
            criteria: {},
            filter_value: {
                type: '',
                data: '',
            },
        },
    ];
}

function removeCondition(condition) {
    conditions.value = conditions.value.filter((item) => item.id !== condition.id);
}
</script>

<template>
    <div class="w-full flex flex-col gap-6 px-6 pb-10">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
                <div class="text-base sm:text-lg font-bold text-gray-800 mb-3">
                    {{ title }}
                </div>
                <p
                    v-if="description"
                    class="text-sm text-gray-500"
                >
                    {{ description }}
                </p>
            </div>

            <button
                type="button"
                class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center justify-center self-start disabled:opacity-60 disabled:cursor-not-allowed"
                :disabled="isSyncingAny"
                @click="syncFromBackend()"
            >
                <LoadingSvg
                    v-if="isSyncingAny"
                    class="size-3 mr-2 animate-spin"
                />
                <RefreshSvg
                    v-else
                    class="size-4 mr-2 fill-blue-600"
                />
                <span>
                    Синхронизировать всё
                </span>
            </button>
        </div>

        <div
            v-if="showDirections || showStages"
            :class="stageGridClass"
        >
            <div
                v-if="showDirections"
                class="transition-opacity duration-200"
                :class="{ 'opacity-70': isSyncingDirections }"
            >
                <div class="flex items-center justify-between gap-2 mb-2">
                    <div class="text-sm font-medium text-gray-700">
                        Выбор направления
                    </div>
                    <button
                        v-if="showDirectionsSync"
                        type="button"
                        class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSyncingDirections"
                        @click="syncDirections()"
                    >
                        <LoadingSvg
                            v-if="isSyncingDirections"
                            class="size-3 mr-1.5 animate-spin"
                        />
                        <RefreshSvg
                            v-else
                            class="size-3.5 mr-1.5 fill-blue-600"
                        />
                        <span>Синхронизировать</span>
                    </button>
                </div>
                <MultiSelectDropdown
                    v-model="directions"
                    :values="directionOptions"
                    label="Выберите направления"
                    :is-loading="isSyncingDirections"
                    is-loading-label="Синхронизация направлений..."
                    searchable
                    search-placeholder="Поиск направления..."
                />
            </div>

            <div
                v-if="showStages"
                class="transition-opacity duration-200"
                :class="{ 'opacity-70': isSyncingStages }"
            >
                <div class="flex items-center justify-between gap-2 mb-2">
                    <div class="text-sm font-medium text-gray-700">
                        Выбор стадии
                    </div>
                    <button
                        v-if="showStagesSync"
                        type="button"
                        class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSyncingStages"
                        @click="syncStages()"
                    >
                        <LoadingSvg
                            v-if="isSyncingStages"
                            class="size-3 mr-1.5 animate-spin"
                        />
                        <RefreshSvg
                            v-else
                            class="size-3.5 mr-1.5 fill-blue-600"
                        />
                        <span>Синхронизировать</span>
                    </button>
                </div>
                <MultiSelectDropdown
                    v-model="stages"
                    :values="stageOptions"
                    label="Выберите стадии"
                    :is-loading="isSyncingStages"
                    is-loading-label="Синхронизация стадий..."
                    searchable
                    search-placeholder="Поиск стадии..."
                    show-select-all
                />
            </div>
        </div>

        <div class="w-full">
            <div class="text-sm font-medium text-gray-700 mb-3">
                Фильтровать по
            </div>
            <div class="flex flex-col sm:flex-row gap-3">
                <button
                    v-for="option in dateFieldOptions"
                    :key="option.value"
                    type="button"
                    class="flex-1 px-4 py-3 rounded-md border-2 text-left transition-colors duration-150"
                    :class="dateField === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-blue-200 text-gray-700 hover:bg-blue-50/60'"
                    @click="dateField = option.value"
                >
                    <div class="text-sm font-semibold">
                        {{ option.label }}
                    </div>
                    <div class="text-xs mt-1 opacity-80">
                        {{ option.description }}
                    </div>
                </button>
            </div>

            <UploadDateRangeFields
                v-model:start-date="startDate"
                v-model:end-date="endDate"
                title="Период фильтрации"
                :id-prefix="dateRangeIdPrefix"
            />
        </div>

        <div class="w-full flex flex-col items-center">
            <SelectDropdown
                class="w-full"
                v-model="conditionTypeValue"
                :values="conditionTypes"
                label="Тип условия"
                :format-function="(item) => item.name"
                :is-error="false"
            />

            <div
                class="mt-5 w-full border border-black rounded-md flex flex-col items-center transition-opacity duration-200"
                :class="{ 'opacity-70': isSyncingFields }"
            >
                <div class="w-[calc(100%-20px)] mt-4 flex items-center justify-between gap-2">
                    <div class="text-sm font-medium text-gray-700">
                        Поля условий
                    </div>
                    <button
                        v-if="showFieldsSync"
                        type="button"
                        class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSyncingFields"
                        @click="syncFields()"
                    >
                        <LoadingSvg
                            v-if="isSyncingFields"
                            class="size-3 mr-1.5 animate-spin"
                        />
                        <RefreshSvg
                            v-else
                            class="size-3.5 mr-1.5 fill-blue-600"
                        />
                        <span>Синхронизировать поля</span>
                    </button>
                </div>

                <div
                    v-for="(condition, index) in conditions"
                    :key="condition.id"
                    class="w-[calc(100%-20px)] my-4"
                >
                    <BitrixDealConditionItem
                        v-model="conditions[index]"
                        :fields="fieldOptions"
                        :criteria="criteria"
                        :is-fields-loading="isSyncingFields"
                        @remove-condition="removeCondition(condition)"
                    />
                </div>

                <div
                    class="w-[calc(100%-40px)] flex items-center justify-center hover:bg-blue-50 my-4 rounded-md transition-all cursor-pointer duration-200"
                    @click="addCondition"
                >
                    <div class="flex items-center justify-center gap-3 py-4">
                        <PlusSvg class="size-6 stroke-2 stroke-blue-600" />
                        <div class="font-bold text-blue-600">
                            Добавить условие
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
